import React, { useState, useCallback } from "react";
import { StyleSheet, View } from "react-native";
import { useFocusEffect } from "expo-router";

import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import ThemedScreen from "@/components/ThemedScreen";
import { CustomAlert } from "@/components/ThemedAlert";
import {
  ThemedList,
  type ISortOption,
  type IGroupOption,
  type ICustomFilterOption,
  type ThemedListViewMode,
} from "@/components/ThemedList";
import { ActivityCard } from "@/components/activity/ActivityCard";

import { useFirebase } from "@components/firebase/useFirebase";
import { useThemeColor } from "@/hooks/useThemeColor";
import { getAllActivity } from "@/services/firebase/activity-functions";
import { ActivityLogModel } from "common247";
import { Spacing } from "@/constants/ThemeColours";

const ADMIN_ACTIVITY_SORT_OPTIONS: ISortOption<ActivityLogModel>[] = [
  { key: "createdAt_desc", label: "Date (Newest First)", getValue: (a) => a.createdAt || 0 },
  { key: "createdAt_asc", label: "Date (Oldest First)", getValue: (a) => a.createdAt || 0, compareFn: (a, b) => (a.createdAt || 0) - (b.createdAt || 0) },
  { key: "actor", label: "User / Actor", getValue: (a) => a.actorDisplayName || a.userId || "" },
  { key: "targetType", label: "Entity Type", getValue: (a) => a.targetType || "" },
  { key: "action", label: "Action", getValue: (a) => a.action || "" },
  { key: "targetTitle", label: "Target Title", getValue: (a) => a.targetTitle || a.description || "" },
];

function getDatePeriodGroup(timestamp: number): string {
  if (!timestamp) return "Unknown Date";
  const now = new Date();
  const date = new Date(timestamp);

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;
  const startOfLast7Days = startOfToday - 7 * 24 * 60 * 60 * 1000;
  const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();

  if (timestamp >= startOfToday) return "Today";
  if (timestamp >= startOfYesterday) return "Yesterday";
  if (timestamp >= startOfLast7Days) return "Past 7 Days";
  if (timestamp >= startOfThisMonth) return "Earlier This Month";

  return date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

const ADMIN_ACTIVITY_GROUP_OPTIONS: IGroupOption<ActivityLogModel>[] = [
  {
    key: "datePeriod",
    label: "Date Period",
    getGroupValue: (a) => getDatePeriodGroup(a.createdAt),
  },
  {
    key: "actor",
    label: "Actor / User",
    getGroupValue: (a) => a.actorDisplayName ? `${a.actorDisplayName} (${a.actorEmail || a.userId})` : `User ${a.userId}`,
  },
  {
    key: "targetType",
    label: "Entity Type",
    getGroupValue: (a) => {
      const type = a.targetType || "other";
      return type.charAt(0).toUpperCase() + type.slice(1) + " Activities";
    },
  },
  {
    key: "action",
    label: "Action",
    getGroupValue: (a) => {
      switch (a.action) {
        case "create":
          return "Created Items";
        case "update":
          return "Updated Items";
        case "delete":
          return "Deleted Items";
        case "logon":
        case "signout":
        case "signup":
          return "Authentication & Sessions";
        case "invite_contact":
          return "Contact Invitations";
        case "terminate_account":
          return "Account Terminations";
        default:
          return "Other Actions";
      }
    },
  },
];

const ADMIN_ACTIVITY_CUSTOM_FILTERS: ICustomFilterOption<ActivityLogModel>[] = [
  {
    key: "targetTypeFilter",
    label: "Entity",
    options: [
      { label: "All Entities", value: "all" },
      { label: "Memories", value: "memory" },
      { label: "Media", value: "media" },
      { label: "Contacts", value: "contact" },
      { label: "Groups", value: "group" },
      { label: "Shares", value: "share" },
      { label: "Messages", value: "message" },
      { label: "Auth / Users", value: "auth" },
    ],
    defaultValue: "all",
    predicate: (item, val) => val === "all" || item.targetType === val,
  },
  {
    key: "actionFilter",
    label: "Action",
    options: [
      { label: "All Actions", value: "all" },
      { label: "Create", value: "create" },
      { label: "Update", value: "update" },
      { label: "Delete", value: "delete" },
      { label: "Logon", value: "logon" },
      { label: "Sign out", value: "signout" },
      { label: "Sign up", value: "signup" },
      { label: "Invite", value: "invite_contact" },
      { label: "Terminate", value: "terminate_account" },
    ],
    defaultValue: "all",
    predicate: (item, val) => val === "all" || item.action === val,
  },
];

/**
 * Admin Screen: Displays platform-wide activity logs across all users,
 * objects, and significant system events.
 */
export default function ActivityAdminScreen() {
  const services = useFirebase();
  const textMuted = useThemeColor({}, "textMuted");

  const [activities, setActivities] = useState<ActivityLogModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  /**
   * Fetches all activity logs across the platform.
   */
  const fetchAllActivities = useCallback(async (isRefresh = false) => {
    if (!services) return;
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await getAllActivity(services, { limit: 100 });
      if (response && response.status) {
        setActivities(response.activities || []);
      } else {
        CustomAlert.alert("Error", response?.error || "Failed to load platform activity logs.");
      }
    } catch (error) {
      console.error("Error fetching all activity logs:", error);
      CustomAlert.alert("Error", "An unexpected error occurred while fetching platform activity.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [services]);

  useFocusEffect(
    useCallback(() => {
      fetchAllActivities();
    }, [fetchAllActivities])
  );

  return (
    <ThemedScreen
      title="All Activity"
      loading={loading}
      scrollable={false}
    >
      <ThemedView style={styles.container}>
        <ThemedList<ActivityLogModel>
          data={activities}
          renderItem={(item: ActivityLogModel, index: number, isHovered: boolean, viewMode: ThemedListViewMode) => (
            <ActivityCard
              activity={item}
              viewMode={viewMode}
              isHovered={isHovered}
              isTimeline={true}
            />
          )}
          keyExtractor={(item) => item.id || `${item.userId}_${item.createdAt}_${item.action}`}
          sortOptions={ADMIN_ACTIVITY_SORT_OPTIONS}
          defaultSortKey="createdAt_desc"
          defaultSortDirection="desc"
          groupOptions={ADMIN_ACTIVITY_GROUP_OPTIONS}
          defaultGroupKey="datePeriod"
          showViewSelector={false}
          defaultViewMode="full"
          viewMode="full"
          columns={1}
          customFilters={ADMIN_ACTIVITY_CUSTOM_FILTERS}
          filterPlaceholder="Search all activities, users, entities..."
          emptyTitle="No activity records found"
          emptyDescription="Platform activity entries will appear as users interact with the system."
          emptyText="No activity records found. Platform activity entries will appear as users interact with the system."
          isRefreshing={refreshing}
          onRefresh={() => fetchAllActivities(true)}
          loading={loading}
          enableHover={true}
        />
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
